import { useState } from 'react';

import {
  AccessAutocomplete,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
  Typography,
} from '@changegps/horizon-component-library';
import { Box, MenuItem } from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { useWatch } from 'react-hook-form';

import { ControlledInputs } from '@/components';

import {
  useDeleteEmailTemplate,
  useGetEmailTemplates,
  useGlobalLetterFormContext,
  useResetEmailTemplates,
  useSaveEmailTemplate,
} from '../../hooks';
import {
  hasEmailTemplateChanged,
  resetSelectedEmailTemplate,
  updateSelectedEmailTemplate,
  useSelectedEmailTemplate,
} from '../../store';

import { DeleteEmailTemplateDialog } from './DeleteEmailTemplateDialog';

export const EditEmailTemplates = () => {
  const form = useGlobalLetterFormContext();
  const { data: emailTemplates, isLoading: isFetching } =
    useGetEmailTemplates();
  const selectedTemplate = useSelectedEmailTemplate();
  const [inputValue, setInputValue] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { mutate: saveEmailTemplate, isPending: isSaving } =
    useSaveEmailTemplate();
  const { mutate: deleteEmailTemplate, isPending: isDeleting } =
    useDeleteEmailTemplate();
  const resetEmailTemplates = useResetEmailTemplates();

  const handleSave = () => {
    const values = form.getValues();

    if (!selectedTemplate?.id) return;

    const payload = {
      template_id: selectedTemplate?.id,
      name: selectedTemplate?.name,
      content: values.email_template.content,
    };

    saveEmailTemplate(payload, {
      onSuccess: () => {
        resetEmailTemplates();
      },
      onSettled: () => {
        updateSelectedEmailTemplate({
          ...selectedTemplate,
          content: values.email_template.content,
        });
      },
    });
  };

  const contentValue = useWatch({
    control: form.control,
    name: 'email_template.content',
  });

  const isChanged = selectedTemplate
    ? hasEmailTemplateChanged({ ...selectedTemplate, content: contentValue })
    : false;

  const handleAcceptDelete = () => {
    if (!selectedTemplate?.id) return;

    deleteEmailTemplate(selectedTemplate?.id, {
      onSuccess: () => {
        resetEmailTemplates();
        form.setValue(`email_template.content`, '');
      },
      onSettled: () => {
        setIsDeleteDialogOpen(false);
        resetSelectedEmailTemplate();
      },
    });
  };

  return (
    <Box
      sx={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          Template:
        </Typography>
        <Box sx={{ width: '40%' }}>
          <AccessAutocomplete
            disabled={isFetching}
            options={emailTemplates || []}
            TextFieldProps={{
              placeholder: isFetching
                ? 'Fetching templates...'
                : 'Select a template',
            }}
            onChange={(event, value) => {
              event.stopPropagation();
              if (
                value === null ||
                typeof value === 'string' ||
                Array.isArray(value)
              )
                return;

              updateSelectedEmailTemplate(value);
              form.setValue('email_template.content', value.content);
            }}
            value={selectedTemplate}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              event?.stopPropagation && event.stopPropagation();
              setInputValue(newInputValue);
            }}
            isOptionEqualToValue={(option, value) => {
              if (!value || value.id === null) return false; // Prevent matching default empty state
              return option.id === value.id;
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.name;
            }}
            renderOption={(props, option, { inputValue }) => {
              if (option.name === null) return null;

              const matches = match(option.name, inputValue, {
                insideWords: true,
              });
              const parts = parse(option.name, matches);

              return (
                <MenuItem {...props} key={option.id}>
                  {parts.map((part, index) => (
                    <Box
                      component="span"
                      key={index}
                      sx={{
                        fontSize: 13,
                        fontWeight: part.highlight ? 700 : 400,
                        marginRight: part.text.endsWith(' ') ? 0.4 : 0,
                        marginLeft: part.text.startsWith(' ') ? 0.4 : 0,
                      }}
                    >
                      {part.text}
                    </Box>
                  ))}
                </MenuItem>
              );
            }}
          />
        </Box>
      </Box>
      <Box>
        <ControlledInputs.TextEditor
          ControllerProps={{
            control: form.control,
            name: `email_template.content`,
          }}
          modules={{
            toolbar: [
              ['bold', 'italic'],
              [{ list: 'ordered' }, { list: 'bullet' }],
            ],
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <SecondaryButton>View all merge fields</SecondaryButton>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TertiaryButton
            disabled={!selectedTemplate?.id || selectedTemplate?.id === '0'}
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            Delete
          </TertiaryButton>
          <PrimaryButton
            onClick={handleSave}
            disabled={isSaving || !selectedTemplate?.id || !isChanged}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </PrimaryButton>
        </Box>
      </Box>

      <DeleteEmailTemplateDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        isPending={isDeleting}
        onConfirm={handleAcceptDelete}
      />
    </Box>
  );
};
